import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toLocalYMD } from '../../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { getCardinalFromDegrees } from '../utils/windUtils';
import { huntService } from '../../features/services/huntService';
import { weatherService } from '../services/weatherService';
import { emitHuntCompleted } from '../../../utils/huntEvents';
import {
  resolveHuntLocationNumericId,
  resolveStandCoordinates,
  standNameFromLocation,
  formatOptimalWindsLabel,
  buildTagInConditionsCard,
  buildStandLogPayload,
  buildStandConditionRows,
} from '../utils/standSheetUtils';
import { HuntLoggedDoneActions } from './HuntLoggedDoneActions';
import './SignOutModal.css';

const SIT_PERIODS = [
  { value: 'morning', label: 'MORNING', time: 'before 10a' },
  { value: 'midday', label: 'MIDDAY', time: '10a — 2p' },
  { value: 'afternoon', label: 'AFTERNOON', time: 'after 2p' },
];

const SAW_KEYS = ['Bucks', 'Does', 'Fawns'];
const HARVEST_OPTS = [
  { id: 'none', label: 'Nothing down — just a sit' },
  { id: 'buck', label: 'Buck' },
  { id: 'doe', label: 'Doe' },
];

const EMPTY_SAW = { Bucks: 0, Does: 0, Fawns: 0 };

function periodLabel(period) {
  if (!period) return '';
  return String(period).replace(/_/g, ' ').toUpperCase();
}

/**
 * Tag-in + stand-entry log sheet for club stands (field-edition enamel).
 * Open stands: pick sit → tag in.
 * Your occupied stand: log (3 steps) or give up the stand.
 */
export function SignOutModal({
  show,
  location,
  occupancy = null,
  clubId = null,
  /** When opening from Overview End Hunt, jump straight into the log steps */
  initialStep = 'detail',
  onSignOut,
  onSignIn,
  onClose,
  onStandReleased,
  isPresident = false,
  clubMembers = [],
  currentUser = null,
  loading = false,
  error = null,
}) {
  const [step, setStep] = useState('detail');
  const [timePeriod, setTimePeriod] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [formError, setFormError] = useState('');
  const [outlookLoading, setOutlookLoading] = useState(false);
  const [outlookError, setOutlookError] = useState('');
  const [outlookDay, setOutlookDay] = useState(null);
  const [huntWeather, setHuntWeather] = useState(null);
  const [huntWeatherLoading, setHuntWeatherLoading] = useState(false);
  const [currentLocationId, setCurrentLocationId] = useState(null);
  const [saw, setSaw] = useState(EMPTY_SAW);
  const [harvest, setHarvest] = useState('none');
  const [hWeight, setHWeight] = useState('');
  const [hPoints, setHPoints] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sessionKey, setSessionKey] = useState(null);
  const finishLockedRef = useRef(false);
  const pendingCompletedRef = useRef(null);
  const navigate = useNavigate();

  const isOccupied = Boolean(occupancy?.isOccupied);
  const isMine = Boolean(occupancy?.isMine);
  const sitPeriod = occupancy?.timePeriod || timePeriod;
  const locationIdKey = resolveHuntLocationNumericId(location);

  const resetLogState = useCallback(() => {
    setSaw(EMPTY_SAW);
    setHarvest('none');
    setHWeight('');
    setHPoints('');
    setNotes('');
    setStep('detail');
  }, []);

  // Only reset when the sheet opens or the stand identity changes — not when
  // clubMembers/currentUser get new references after listClubFeatures refresh
  // (that was wiping the "IN THE BOOKS" step immediately after submit).
  useEffect(() => {
    if (!show || !location || locationIdKey == null) {
      if (!show) {
        setSessionKey(null);
        finishLockedRef.current = false;
      }
      return;
    }

    // After a successful log, keep the stamp until the hunter dismisses it.
    if (finishLockedRef.current) return;

    const nextKey = `${locationIdKey}:${occupancy?.isOccupied ? 'occ' : 'open'}:${occupancy?.timePeriod || ''}:${initialStep || 'detail'}`;
    if (sessionKey === nextKey) return;

    setSessionKey(nextKey);
    setTimePeriod('');
    setFormError('');
    resetLogState();
    if (initialStep && initialStep !== 'detail') {
      setStep(initialStep);
    }

    if (!isPresident && clubMembers.length > 0 && currentUser) {
      const currentUserInClub = clubMembers.find(
        (member) => parseInt(member.id, 10) === parseInt(currentUser.id, 10)
      );
      if (currentUserInClub) {
        setSelectedUserId(currentUserInClub.id);
      } else {
        setSelectedUserId(null);
      }
    } else {
      setSelectedUserId(null);
    }

    (async () => {
      try {
        setOutlookLoading(true);
        setOutlookError('');
        setOutlookDay(null);
        setCurrentLocationId(locationIdKey);
        const resp = await api.get(`/hunt-locations/${locationIdKey}/optimal_days/?days=1`);
        const body = resp?.data || {};
        const day = Array.isArray(body.days) && body.days.length ? body.days[0] : null;
        setOutlookDay(day);
      } catch (e) {
        setOutlookError('Failed to load outlook');
      } finally {
        setOutlookLoading(false);
      }
    })();
  }, [
    show,
    location,
    locationIdKey,
    occupancy?.isOccupied,
    occupancy?.timePeriod,
    sessionKey,
    initialStep,
    isPresident,
    clubMembers,
    currentUser,
    resetLogState,
  ]);

  // Same weather source as HuntLogPage — richer than optimal_days outlook.
  useEffect(() => {
    if (!show || !occupancy?.isOccupied || !occupancy?.timePeriod || !location) {
      setHuntWeather(null);
      setHuntWeatherLoading(false);
      return undefined;
    }
    const coords = resolveStandCoordinates(location);
    if (!coords?.lat || !coords?.lng) {
      setHuntWeather(null);
      setHuntWeatherLoading(false);
      return undefined;
    }
    let cancelled = false;
    setHuntWeatherLoading(true);
    const date = occupancy.date || toLocalYMD(new Date());
    const period = String(occupancy.timePeriod).toLowerCase();
    Promise.resolve(
      weatherService.getHuntWeatherData(coords.lat, coords.lng, date, [period])
    )
      .then((data) => {
        if (!cancelled) setHuntWeather(data || null);
      })
      .catch(() => {
        if (!cancelled) setHuntWeather(null);
      })
      .finally(() => {
        if (!cancelled) setHuntWeatherLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    show,
    location,
    occupancy?.isOccupied,
    occupancy?.timePeriod,
    occupancy?.date,
    locationIdKey,
  ]);

  const handleSignOut = async () => {
    if (!timePeriod) {
      setFormError('Pick a sit to tag in');
      return;
    }

    if (isPresident && !selectedUserId) {
      setFormError('Select who is tagging in');
      return;
    }

    if (!isPresident && !selectedUserId) {
      setFormError('User not found');
      return;
    }

    setFormError('');

    try {
      const today = toLocalYMD(new Date());
      const locationId = resolveHuntLocationNumericId(location);
      if (!locationId) {
        throw new Error('Could not resolve location id');
      }
      await onSignOut(locationId, selectedUserId, today, timePeriod);
      onClose();
    } catch (err) {
      console.error('Sign out failed:', err);
      setFormError(err.message || 'Failed to tag in');
    }
  };

  const handleGiveUpStand = async () => {
    setFormError('');
    try {
      const locationId = resolveHuntLocationNumericId(location);
      if (!locationId) throw new Error('Could not resolve location id');
      const date = occupancy?.date || toLocalYMD(new Date());
      const period = occupancy?.timePeriod;
      if (!period) throw new Error('Missing sit period');
      if (typeof onSignIn !== 'function') throw new Error('Sign-in handler missing');
      await onSignIn(locationId, date, period, {
        userId: occupancy?.occupiedByUserId ?? null,
        requireRemoved: true,
      });
      if (typeof onStandReleased === 'function') {
        try {
          await onStandReleased();
        } catch (_) {}
      }
      onClose();
    } catch (err) {
      console.error('Give up stand failed:', err);
      setFormError(err.message || 'Failed to clear stand');
    }
  };

  const handleSubmitLog = async () => {
    setFormError('');
    setSubmitting(true);
    try {
      const locationId = resolveHuntLocationNumericId(location);
      if (!locationId) throw new Error('Could not resolve location id');
      const date = occupancy?.date || toLocalYMD(new Date());
      const period = occupancy?.timePeriod;
      if (!period) throw new Error('Missing sit period');

      const weatherFromHunt =
        huntWeather && Object.keys(huntWeather).length > 0
          ? huntWeather
          : outlookDay?.weather
            ? { [String(period).toLowerCase()]: outlookDay.weather }
            : {};
      const payload = buildStandLogPayload({
        date,
        timePeriod: period,
        locationName: standNameFromLocation(location),
        clubId,
        huntLocationId: locationId,
        saw,
        harvest,
        weight: hWeight,
        points: hPoints,
        notes,
        weatherData: weatherFromHunt,
      });

      const coords = resolveStandCoordinates(location);
      await huntService.createHunt(payload, coords);

      // Release stand without refreshing map yet — refresh on stamp dismiss so
      // the "IN THE BOOKS" step isn't fighting feature reloads + toasts.
      if (typeof onSignIn === 'function') {
        await onSignIn(locationId, date, period, {
          userId: occupancy?.occupiedByUserId ?? null,
          requireRemoved: true,
          refresh: false,
        });
      }

      pendingCompletedRef.current = {
        locationId,
        date,
        timePeriod: period,
        userId: occupancy?.occupiedByUserId || currentUser?.id || null,
        clubId: clubId != null ? Number(clubId) : null,
      };

      finishLockedRef.current = true;
      setStep('done');
      // Stamp is the finish signal — no toast, no map refresh until dismiss.
    } catch (err) {
      console.error('Log hunt failed:', err);
      finishLockedRef.current = false;
      pendingCompletedRef.current = null;
      const apiMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === 'string' ? err.response.data : null);
      setFormError(apiMsg || err.message || 'Failed to log hunt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    const wasFinished = finishLockedRef.current;
    const completedDetail = pendingCompletedRef.current;
    setTimePeriod('');
    setSelectedUserId(null);
    setFormError('');
    finishLockedRef.current = false;
    pendingCompletedRef.current = null;
    resetLogState();

    // Emit after stamp dismiss so map refresh doesn't fight the finish UI.
    if (wasFinished && completedDetail) {
      try {
        emitHuntCompleted(completedDetail);
      } catch (_) {}
    }

    onClose();
  };

  if (!show || !location) return null;

  const name = standNameFromLocation(location);
  const notesText = location.notes || location.properties?.notes || '';

  const optimalWindsLabel = formatOptimalWindsLabel(location);
  const conditionsCard = buildTagInConditionsCard({
    location,
    outlookDay,
    huntWeather,
    sitPeriod,
    outlookLoading,
  });

  const badge = isOccupied
    ? isMine
      ? 'YOU'
      : (occupancy?.occupiedBy || name)
          .split(/\s+/)
          .filter(Boolean)
          .map((p) => p[0])
          .join('')
          .slice(0, 2)
          .toUpperCase() || '·'
    : name
        .split(/\s+/)
        .filter(Boolean)
        .map((p) => p[0])
        .join('')
        .slice(0, 2)
        .toUpperCase() || '·';

  let kicker = 'OPEN · TAG IN TODAY';
  if (isOccupied) {
    if (isMine) {
      kicker = sitPeriod
        ? `YOU ARE ON STAND · ${periodLabel(sitPeriod)}`
        : 'YOU ARE ON STAND';
    } else {
      kicker = `${String(occupancy?.occupiedBy || 'HUNTER').toUpperCase()} · ${periodLabel(sitPeriod) || 'ON STAND'}`;
    }
  }

  const sitReady = Boolean(timePeriod) && (!isPresident || selectedUserId);
  const ctaLabel = timePeriod
    ? `TAG IN · ${String(timePeriod).toUpperCase()} SIT`
    : 'PICK A SIT TO TAG IN';

  let windVerdict = 'CHECKING WIND…';
  const condRows = buildStandConditionRows({
    standName: name,
    sitPeriod: sitPeriod,
    date: occupancy?.date || toLocalYMD(new Date()),
    huntWeather,
    outlookWeather: outlookDay?.weather || null,
  });
  if (outlookDay?.weather) {
    const w = outlookDay.weather;
    const windDeg = typeof w.wind_deg === 'number' ? w.wind_deg : null;
    const windCard = windDeg != null ? getCardinalFromDegrees(windDeg) : null;
    const windSpeed = typeof w.wind_speed === 'number' ? Math.round(w.wind_speed) : null;
    windVerdict = [
      windSpeed != null ? `${windSpeed} mph` : null,
      windCard,
      optimalWindsLabel ? `optimal ${optimalWindsLabel}` : null,
    ]
      .filter(Boolean)
      .join(' · ')
      .toUpperCase();
  } else if (outlookError) {
    windVerdict = 'OUTLOOK UNAVAILABLE';
  } else if (!outlookLoading && optimalWindsLabel) {
    windVerdict = `OPTIMAL WIND: ${String(optimalWindsLabel).toUpperCase()}`;
  }

  // Enrich footer wind from hunt weather when available
  if (huntWeather && sitPeriod) {
    const pw = huntWeather[String(sitPeriod).toLowerCase()];
    if (pw) {
      windVerdict = [
        pw.wind_speed != null ? `${Math.round(pw.wind_speed)} mph` : null,
        pw.wind_direction_text || null,
        optimalWindsLabel ? `optimal ${optimalWindsLabel}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
        .toUpperCase();
    }
  }

  const showHarvestFields = harvest === 'buck' || harvest === 'doe';
  const busy = loading || submitting;

  const renderConditionsCard = () => {
    const windDisplay = conditionsCard.loading && !conditionsCard.windLabel
      ? '…'
      : (conditionsCard.windLabel || '—');
    const scoreDisplay = conditionsCard.loading && conditionsCard.score == null
      ? '…'
      : (conditionsCard.score != null ? String(conditionsCard.score) : '—');
    return (
      <div className="stand-tagin-sheet__wx" data-testid="stand-tagin-wx">
        <div className="stand-tagin-sheet__wx-row">
          <span className="stand-tagin-sheet__wx-k">Wind</span>
          <span className="stand-tagin-sheet__wx-v">
            {windDisplay}
            {conditionsCard.isOptimal ? (
              <span className="stand-tagin-sheet__wx-flag">Optimal</span>
            ) : null}
          </span>
        </div>
        <div className="stand-tagin-sheet__wx-row">
          <span className="stand-tagin-sheet__wx-k">Score</span>
          <span className="stand-tagin-sheet__wx-v">{scoreDisplay}</span>
        </div>
      </div>
    );
  };

  const stepMeta = {
    saw: { label: 'STEP 1 OF 3', pct: 33 },
    harvest: { label: 'STEP 2 OF 3', pct: 66 },
    cond: { label: 'STEP 3 OF 3', pct: 100 },
  };

  const renderProgress = (key) => {
    const meta = stepMeta[key];
    if (!meta) return null;
    return (
      <div className="stand-tagin-sheet__progress">
        <span className="stand-tagin-sheet__progress-label">{meta.label}</span>
        <span className="stand-tagin-sheet__progress-track" aria-hidden>
          <span
            className="stand-tagin-sheet__progress-fill"
            style={{ width: `${meta.pct}%` }}
          />
        </span>
      </div>
    );
  };

  const renderDetail = () => {
    if (!isOccupied) {
      return (
        <>
          {renderConditionsCard()}
          {notesText ? (
            <div className="stand-tagin-sheet__note">{notesText}</div>
          ) : null}

          {isPresident && (
            <div className="stand-tagin-sheet__field">
              <div className="stand-tagin-sheet__label">TAG IN FOR</div>
              <select
                className="stand-tagin-sheet__select"
                value={selectedUserId || ''}
                onChange={(e) =>
                  setSelectedUserId(e.target.value ? parseInt(e.target.value, 10) : null)
                }
                aria-label="Member to tag in"
              >
                <option value="">Select member…</option>
                {clubMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.first_name && member.last_name
                      ? `${member.first_name} ${member.last_name}`
                      : member.first_name || member.username}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="stand-tagin-sheet__field">
            <div className="stand-tagin-sheet__label">PICK YOUR SIT · TODAY ONLY</div>
            <div className="stand-tagin-sheet__periods" role="group" aria-label="Sit period">
              {SIT_PERIODS.map((p) => {
                const on = timePeriod === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    className={`stand-tagin-sheet__period${on ? ' is-active' : ''}`}
                    aria-pressed={on}
                    onClick={() => setTimePeriod(p.value)}
                  >
                    <span className="stand-tagin-sheet__period-label">{p.label}</span>
                    <span className="stand-tagin-sheet__period-time">{p.time}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            className={`stand-tagin-sheet__cta${sitReady ? ' is-ready' : ''}`}
            disabled={busy || !sitReady}
            onClick={handleSignOut}
            data-testid="stand-tagin-cta"
          >
            {busy ? 'TAGGING IN…' : ctaLabel}
          </button>
        </>
      );
    }

    if (isMine) {
      return (
        <>
          {notesText ? (
            <div className="stand-tagin-sheet__note">{notesText}</div>
          ) : (
            <div className="stand-tagin-sheet__note">
              You’re tagged in
              {sitPeriod ? ` for the ${String(sitPeriod).toLowerCase()} sit` : ''}. Log the hunt
              when you’re back at camp, or give up the stand without logging.
            </div>
          )}
          <div className="stand-tagin-sheet__actions">
            <button
              type="button"
              className="stand-tagin-sheet__cta is-ready is-loden"
              disabled={busy}
              onClick={() => setStep('saw')}
              data-testid="stand-log-hunt-cta"
            >
              BACK AT CAMP · LOG THE HUNT
            </button>
            <button
              type="button"
              className="stand-tagin-sheet__cta is-ready is-ghost"
              disabled={busy}
              onClick={handleGiveUpStand}
              data-testid="stand-give-up-cta"
            >
              {busy ? 'CLEARING…' : 'GIVE UP THE STAND'}
            </button>
          </div>
        </>
      );
    }

    if (isPresident) {
      return (
        <>
          <div className="stand-tagin-sheet__note">
            {occupancy?.occupiedBy || 'A member'} is on this stand
            {sitPeriod ? ` · ${periodLabel(sitPeriod)} sit` : ''}. Presidents can clear it.
          </div>
          <button
            type="button"
            className="stand-tagin-sheet__cta is-ready"
            disabled={busy}
            onClick={handleGiveUpStand}
            data-testid="stand-clear-pres-cta"
          >
            {busy ? 'CLEARING…' : 'CLEAR STAND · PRESIDENT'}
          </button>
        </>
      );
    }

    return (
      <>
        <div className="stand-tagin-sheet__note">
          {occupancy?.occupiedBy || 'Someone'} has this stand
          {sitPeriod ? ` for the ${String(sitPeriod).toLowerCase()} sit` : ''}. Pick another.
        </div>
        <button
          type="button"
          className="stand-tagin-sheet__cta"
          disabled
          data-testid="stand-occupied-cta"
        >
          {`${String(occupancy?.occupiedBy || 'HUNTER').toUpperCase()} HAS IT · PICK ANOTHER`}
        </button>
      </>
    );
  };

  const renderSaw = () => (
    <>
      {renderProgress('saw')}
      <div className="stand-tagin-sheet__headline">What&apos;d you see?</div>
      <div className="stand-tagin-sheet__counters">
        {SAW_KEYS.map((key) => (
          <div key={key} className="stand-tagin-sheet__counter">
            <span className="stand-tagin-sheet__counter-label">{key}</span>
            <button
              type="button"
              className="stand-tagin-sheet__counter-btn"
              aria-label={`Decrease ${key}`}
              onClick={() =>
                setSaw((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - 1) }))
              }
            >
              –
            </button>
            <span className="stand-tagin-sheet__counter-value" data-testid={`saw-${key}`}>
              {saw[key] || 0}
            </span>
            <button
              type="button"
              className="stand-tagin-sheet__counter-btn is-plus"
              aria-label={`Increase ${key}`}
              onClick={() => setSaw((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }))}
            >
              +
            </button>
          </div>
        ))}
      </div>
      <div className="stand-tagin-sheet__nav-row">
        <button
          type="button"
          className="stand-tagin-sheet__secondary"
          onClick={() => setStep('detail')}
        >
          BACK
        </button>
        <button
          type="button"
          className="stand-tagin-sheet__cta is-ready is-loden stand-tagin-sheet__cta--flex"
          onClick={() => setStep('harvest')}
          data-testid="stand-log-next-harvest"
        >
          NEXT · HARVEST
        </button>
      </div>
    </>
  );

  const renderHarvest = () => (
    <>
      {renderProgress('harvest')}
      <div className="stand-tagin-sheet__headline">Put one down?</div>
      <div className="stand-tagin-sheet__harvest-opts">
        {HARVEST_OPTS.map((opt) => {
          const on = harvest === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              className={`stand-tagin-sheet__harvest-opt${on ? ' is-active' : ''}`}
              aria-pressed={on}
              onClick={() => setHarvest(opt.id)}
              data-testid={`harvest-${opt.id}`}
            >
              <span className={`stand-tagin-sheet__harvest-dot${on ? ' is-on' : ''}`} />
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
      {showHarvestFields && (
        <div className="stand-tagin-sheet__harvest-fields">
          <div>
            <div className="stand-tagin-sheet__label">FIELD DRESSED</div>
            <input
              className="stand-tagin-sheet__input"
              value={hWeight}
              onChange={(e) => setHWeight(e.target.value)}
              placeholder="lb"
              inputMode="decimal"
              aria-label="Field dressed weight"
            />
          </div>
          {harvest === 'buck' && (
            <div>
              <div className="stand-tagin-sheet__label">POINTS</div>
              <input
                className="stand-tagin-sheet__input"
                value={hPoints}
                onChange={(e) => setHPoints(e.target.value)}
                placeholder="8"
                inputMode="numeric"
                aria-label="Antler points"
              />
            </div>
          )}
        </div>
      )}
      <div className="stand-tagin-sheet__nav-row">
        <button
          type="button"
          className="stand-tagin-sheet__secondary"
          onClick={() => setStep('saw')}
        >
          BACK
        </button>
        <button
          type="button"
          className="stand-tagin-sheet__cta is-ready is-loden stand-tagin-sheet__cta--flex"
          onClick={() => setStep('cond')}
          data-testid="stand-log-next-cond"
        >
          NEXT · CONDITIONS
        </button>
      </div>
    </>
  );

  const renderCond = () => (
    <>
      {renderProgress('cond')}
      <div className="stand-tagin-sheet__headline">Conditions, pulled for you</div>
      <p className="stand-tagin-sheet__subcopy">
        Recorded at the stand while you sat. Notes go in the book below.
      </p>
      {huntWeatherLoading ? (
        <div className="stand-tagin-sheet__note" data-testid="stand-log-conditions-loading">
          Pulling weather for this sit…
        </div>
      ) : (
        <div className="stand-tagin-sheet__cond" data-testid="stand-log-conditions">
          {condRows.map((row) => (
            <div key={row.k} className="stand-tagin-sheet__cond-row">
              <span className="stand-tagin-sheet__cond-k">{row.k}</span>
              <span className="stand-tagin-sheet__cond-v">{row.v}</span>
            </div>
          ))}
        </div>
      )}
      <div className="stand-tagin-sheet__field">
        <div className="stand-tagin-sheet__label">NOTES FOR THE BOOK</div>
        <textarea
          className="stand-tagin-sheet__textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Two does at the pinch point right at last light."
          rows={3}
          aria-label="Hunt notes"
        />
      </div>
      <div className="stand-tagin-sheet__nav-row">
        <button
          type="button"
          className="stand-tagin-sheet__secondary"
          onClick={() => setStep('harvest')}
          disabled={busy}
        >
          BACK
        </button>
        <button
          type="button"
          className="stand-tagin-sheet__cta is-ready stand-tagin-sheet__cta--flex"
          onClick={handleSubmitLog}
          disabled={busy}
          data-testid="stand-log-submit"
        >
          {busy ? 'SAVING…' : 'PUT IT IN THE BOOKS'}
        </button>
      </div>
    </>
  );

  const renderDone = () => {
    const seen = (saw.Bucks || 0) + (saw.Does || 0) + (saw.Fawns || 0);
    return (
      <div className="stand-tagin-sheet__done" data-testid="stand-log-done">
        <div className="stand-tagin-sheet__stamp">IN THE BOOKS</div>
        <p className="stand-tagin-sheet__done-line">
          {name}
          {sitPeriod ? ` · ${periodLabel(sitPeriod)}` : ''} · {seen} deer seen
          {harvest !== 'none' ? ` · ${harvest}` : ''}
        </p>
        <HuntLoggedDoneActions
          harvested={harvest !== 'none'}
          closeLabel="BACK TO THE BOARD"
          closeTestId="stand-log-done-close"
          onClose={handleClose}
        />
      </div>
    );
  };

  let body = null;
  if (step === 'done') body = renderDone();
  else if (step === 'saw') body = renderSaw();
  else if (step === 'harvest') body = renderHarvest();
  else if (step === 'cond') body = renderCond();
  else body = renderDetail();

  return createPortal(
    <div
      className="stand-tagin-backdrop"
      data-testid="stand-tagin-sheet"
      role="presentation"
      onClick={handleClose}
    >
      <div
        className="stand-tagin-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stand-tagin-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="stand-tagin-sheet__header">
          <span
            className={`stand-tagin-sheet__badge${isOccupied && isMine ? ' is-mine' : ''}${isOccupied && !isMine ? ' is-taken' : ''}`}
            aria-hidden
          >
            {badge}
          </span>
          <span className="stand-tagin-sheet__titles">
            <span id="stand-tagin-title" className="stand-tagin-sheet__name">
              {name}
            </span>
            <span className="stand-tagin-sheet__kicker">{kicker}</span>
          </span>
          <button
            type="button"
            className="stand-tagin-sheet__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="stand-tagin-sheet__body">
          {(error || formError) && step !== 'done' && (
            <div className="stand-tagin-sheet__error" role="alert">
              {error || formError}
            </div>
          )}

          {body}

          {step === 'detail' && !isOccupied && (
            <button
              type="button"
              className="stand-tagin-sheet__link"
              onClick={() =>
                navigate(
                  currentLocationId
                    ? `/hunts?tab=forecast&standId=${currentLocationId}`
                    : '/hunts?tab=forecast'
                )
              }
            >
              OPEN HUNT FORECAST
            </button>
          )}

          {step === 'detail' && isOccupied && (
            <div className="stand-tagin-sheet__footer">WIND {windVerdict}</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
